import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash, FolderOpen, ChartBar, ChartLineUp, Printer } from "@phosphor-icons/react";
import TimeStudyPareto from "@/components/TimeStudyPareto";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmtDur = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;

export default function TimeStudyLibrary({ canWrite, onLoad, onPrint, refreshKey }) {
  const [filters, setFilters] = useState({ area: "", operator: "" });
  const [rows, setRows] = useState([]);
  const [groupBy, setGroupBy] = useState("area");
  const [cmp, setCmp] = useState([]);

  const load = useCallback(() => {
    const p = {}; if (filters.area) p.area = filters.area; if (filters.operator) p.operator = filters.operator;
    api.get("/operations/time-studies", { params: p }).then((r) => setRows(r.data || [])).catch(() => {});
    api.get("/operations/time-studies/compare", { params: { group_by: groupBy } }).then((r) => setCmp(r.data || [])).catch(() => {});
  }, [filters, groupBy]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este estudio?")) return;
    try { await api.delete(`/operations/time-studies/${id}`); toast.success("Estudio eliminado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border rounded-lg p-4 print:hidden" data-testid="ts-library">
      <Tabs defaultValue="list">
        <TabsList className="bg-slate-100 h-auto p-1">
          <TabsTrigger value="list" className="text-xs" data-testid="ts-lib-tab-list"><FolderOpen size={13} className="mr-1" /> Estudios guardados ({rows.length})</TabsTrigger>
          <TabsTrigger value="cmp" className="text-xs" data-testid="ts-lib-tab-compare"><ChartBar size={13} className="mr-1" /> Comparativa</TabsTrigger>
          <TabsTrigger value="pareto" className="text-xs" data-testid="ts-lib-tab-pareto"><ChartLineUp size={13} className="mr-1" /> Pareto de pérdidas</TabsTrigger>
        </TabsList>
        <TabsContent value="pareto" className="mt-3"><TimeStudyPareto refreshKey={refreshKey} /></TabsContent>
        <TabsContent value="list" className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Filtrar por frente / área" value={filters.area} onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))} data-testid="ts-lib-filter-area" />
            <Input placeholder="Filtrar por operador" value={filters.operator} onChange={(e) => setFilters((f) => ({ ...f, operator: e.target.value }))} data-testid="ts-lib-filter-operator" />
          </div>
          <div className="max-h-72 overflow-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0"><tr>
                {["Fecha", "Turno", "Frente", "Operador", "Acts", "Operación", "Utiliz.", ""].map((h) => <th key={h} className="px-2 py-1 text-left font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`ts-lib-row-${r.id}`}>
                    <td className="px-2 py-1">{r.date}</td><td className="px-2 py-1 font-mono">{r.shift}</td>
                    <td className="px-2 py-1">{r.area || "—"}</td><td className="px-2 py-1">{r.operator || "—"}</td>
                    <td className="px-2 py-1">{r.summary?.count}</td><td className="px-2 py-1">{fmtDur(r.summary?.operation_min || 0)}</td>
                    <td className="px-2 py-1 font-bold">{r.summary?.utilization}%</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onLoad(r)} data-testid={`ts-lib-load-${r.id}`}>Cargar</Button>
                      {onPrint && <Button size="sm" variant="ghost" className="h-6 px-1" title="Imprimir / PDF" onClick={() => onPrint(r)} data-testid={`ts-lib-print-${r.id}`}><Printer size={13} /></Button>}
                      {canWrite && <Button size="sm" variant="ghost" className="h-6 px-1 text-red-600" onClick={() => del(r.id)} data-testid={`ts-lib-del-${r.id}`}><Trash size={13} /></Button>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-4">Sin estudios guardados.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="cmp" className="mt-3 space-y-2">
          <div className="flex gap-1">
            {["area", "operator"].map((g) => (
              <Button key={g} size="sm" variant={groupBy === g ? "default" : "outline"} className="h-7 text-xs" onClick={() => setGroupBy(g)} data-testid={`ts-cmp-group-${g}`}>
                Por {g === "area" ? "frente" : "operador"}
              </Button>
            ))}
          </div>
          <table className="w-full text-xs border rounded" data-testid="ts-cmp-table">
            <thead className="bg-slate-100"><tr>
              {[groupBy === "area" ? "Frente" : "Operador", "Estudios", "Total", "Operación", "No productivo", "Utilización"].map((h) => <th key={h} className="px-2 py-1 text-left font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {cmp.map((g) => (
                <tr key={g.key} className="border-t">
                  <td className="px-2 py-1 font-semibold">{g.key}</td><td className="px-2 py-1">{g.studies}</td>
                  <td className="px-2 py-1">{fmtDur(g.total_min)}</td><td className="px-2 py-1">{fmtDur(g.operation_min)}</td>
                  <td className="px-2 py-1">{fmtDur(g.non_productive_min)}</td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-2"><div className="h-2 bg-slate-200 rounded w-24"><div className="h-2 bg-black rounded" style={{ width: `${g.utilization}%` }} /></div><b>{g.utilization}%</b></div>
                  </td>
                </tr>
              ))}
              {cmp.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-4">Sin datos para comparar.</td></tr>}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>
    </div>
  );
}
